import { useCallback, useEffect, useState } from 'react'
import type {
  Emplacement,
  ObjetDTO,
  PersonnageDTO,
  ReputationDTO,
  Stat,
} from '@whatremains/shared'
import { CLASSE_INFO, NOMS_ORIGINES } from '@whatremains/shared'
import { api, ErreurApi } from '../api'
import { Poupee } from '../composants/Poupee'
import { Titres } from '../composants/Titres'

const NOMS_STATS: Record<Stat, string> = {
  force: 'Force',
  adresse: 'Adresse',
  esprit: 'Esprit',
  vigueur: 'Vigueur',
  fortune: 'Fortune',
}

/** Ce que chaque statistique gouverne. Sinon on répartit ses points à l'aveugle. */
const EFFET_STAT: Record<Stat, string> = {
  force: 'les dégâts',
  adresse: 'la précision, l’esquive, le critique',
  esprit: 'la résistance',
  vigueur: 'les points de vie',
  fortune: 'le butin et les prix',
}

const VIDE: Record<Stat, number> = { force: 0, adresse: 0, esprit: 0, vigueur: 0, fortune: 0 }

export function Fiche({
  id,
  surChangement,
  surSortie,
}: {
  id: string
  surChangement: () => void
  surSortie: () => void
}) {
  const [p, setP] = useState<PersonnageDTO | null>(null)
  const [objets, setObjets] = useState<ObjetDTO[]>([])
  const [renoms, setRenoms] = useState<ReputationDTO[]>([])
  const [brouillon, setBrouillon] = useState<Record<Stat, number>>({ ...VIDE })
  const [erreur, setErreur] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    const [perso, inv, rep] = await Promise.all([
      api.personnage(id),
      api.inventaire(id),
      api.reputations(id),
    ])
    setP(perso)
    setObjets(inv)
    setRenoms(rep)
    setBrouillon({ ...VIDE })
  }, [id])

  useEffect(() => { void recharger() }, [recharger])

  if (!p) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  async function agir(action: () => Promise<unknown>) {
    setErreur(null)
    try {
      await action()
      await recharger()
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  const portes = new Map(
    objets.filter((o) => o.porte && o.emplacement).map((o) => [o.emplacement as Emplacement, o]),
  )
  const ranges = objets.filter((o) => !o.porte)
  const repartis = (Object.keys(VIDE) as Stat[]).reduce((s, k) => s + brouillon[k], 0)
  const restants = p.pointsStatLibres - repartis
  const avancement = p.experienceProchainNiveau
    ? Math.min(100, (p.experience / p.experienceProchainNiveau) * 100)
    : 100
  const info = CLASSE_INFO[p.classe]

  // Ce que l'équipement porté ajoute, par statistique. La fiche affichait le
  // total sans jamais dire ce qui venait de soi et ce qui venait des pièces.
  //
  // Attention : `p.stats` est DÉJÀ le total, équipement compris. Afficher
  // « 20 +16 » se lisait donc comme 36. On montre le total, puis la part
  // portée en dessous, parce que c'est le total qui sert au combat.
  const apport = { ...VIDE }
  for (const o of portes.values()) {
    for (const [s, v] of Object.entries(o.bonus)) apport[s as Stat] += v ?? 0
  }

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          Niveau {p.niveau} · {info.nom} · {NOMS_ORIGINES[p.origine]}
        </div>
        <h1>{p.nom}</h1>
        {p.titreActif && <p className="titre-actif">{p.titreActif.nom}</p>}
        <div className="jauge" style={{ maxWidth: 420 }}>
          <div style={{ width: `${avancement}%` }} />
        </div>
        <p className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 11, marginTop: 8 }}>
          {p.experienceProchainNiveau
            ? `${p.experience} / ${p.experienceProchainNiveau}`
            : 'Il n’y a plus de niveau suivant.'}
          {'   ·   '}
          {p.or} or
        </p>
        {erreur && <p className="erreur">{erreur}</p>}
      </div>

      <div className="deux-colonnes">
        <section>
          <div className="surtitre">Ce que tu portes</div>
          <Poupee
            classe={p.classe}
            niveau={p.niveau}
            portes={portes}
            ranges={ranges}
            artefact={p.artefact}
            surRetirer={(o) => void agir(() => api.ranger(id, o.id))}
            surEquiper={(o, e) => void agir(() => api.equiper(id, o.id, e))}
            surConsommer={(o) => void agir(() => api.consommer(id, o.id))}
            surPoser={() => void agir(() => api.poserArtefact(id))}
          />
        </section>

        <div>
          <section>
            <div className="surtitre">Statistiques</div>
            <div className="paires paires-stats">
              {(Object.keys(NOMS_STATS) as Stat[]).map((s) => (
                <div className="paire" key={s}>
                  <b>
                    {NOMS_STATS[s]}
                    <i className="tenu effet-stat">{EFFET_STAT[s]}</i>
                  </b>
                  <span className="chiffre">
                    {p.stats[s]}
                    {brouillon[s] > 0 && <span style={{ color: 'var(--r7)' }}> +{brouillon[s]}</span>}
                    {apport[s] > 0 && <i className="apport">dont +{apport[s]} porté</i>}
                    {p.pointsStatLibres > 0 && (
                      <span style={{ marginLeft: 14 }}>
                        <button
                          className="discret"
                          disabled={restants <= 0}
                          onClick={() => setBrouillon({ ...brouillon, [s]: brouillon[s] + 1 })}
                        >
                          +
                        </button>
                        {brouillon[s] > 0 && (
                          <button
                            className="discret"
                            style={{ marginLeft: 8 }}
                            onClick={() => setBrouillon({ ...brouillon, [s]: brouillon[s] - 1 })}
                          >
                            −
                          </button>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {p.pointsStatLibres > 0 && (
              <p style={{ marginTop: 16 }}>
                <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
                  {restants} point{restants > 1 ? 's' : ''} à placer
                </span>
                {repartis > 0 && (
                  <button
                    className="action"
                    style={{ marginLeft: 18 }}
                    onClick={() => void agir(() => api.repartir(id, brouillon))}
                  >
                    Confirmer
                  </button>
                )}
              </p>
            )}
          </section>

          <section>
            <div className="surtitre">Ce que ça donne</div>
            <div className="paires">
              <div className="paire"><b>Points de vie</b><span className="chiffre">{p.pv} / {p.statsDerivees.pvMax}</span></div>
              <div className="paire"><b>Dégâts</b><span className="chiffre">{p.statsDerivees.degatsMin} à {p.statsDerivees.degatsMax}</span></div>
              <div className="paire"><b>Précision</b><span className="chiffre">{p.statsDerivees.precision}</span></div>
              <div className="paire"><b>Esquive</b><span className="chiffre">{p.statsDerivees.esquive}</span></div>
              <div className="paire"><b>Critique</b><span className="chiffre">{p.statsDerivees.critique} %</span></div>
              <div className="paire"><b>Résistance</b><span className="chiffre">{p.statsDerivees.resistance}</span></div>
            </div>
          </section>

          {renoms.length > 0 && (
            <section>
              <div className="surtitre">Comment on te voit</div>
              <div className="renom-bref">
                {renoms.map((r) => {
                  const etendue = r.palierMax - r.palierMin
                  const part = etendue > 0 ? ((r.valeur - r.palierMin) / etendue) * 100 : 0
                  return (
                    <div className="renom-ligne" key={r.code}>
                      {/*
                        Le métier accolé au nom, ici comme partout ailleurs.

                        Enzo : « ce n'est pas clair quelle faction fait quoi ».
                        C'est le panneau qu'il voit le plus souvent, et il ne
                        portait que cinq noms qui ne disent pas leur métier.
                      */}
                      <b>{r.nom} <i>· {r.metier}</i></b>
                      <div className="renom-barre">
                        <div style={{ width: `${Math.max(2, Math.min(100, part))}%` }} />
                      </div>
                      <span>{r.palier}</span>
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      </div>

      {/*
        Le bouton « Se battre » est parti dans son propre onglet.

        Enzo : « ce serait plus logique à mon sens. » Ça l'est : la fiche décrit
        ce qu'on EST, se battre est quelque chose qu'on FAIT. Le laisser ici en
        plus de l'onglet aurait donné deux portes vers le même écran, dont l'une
        au fond d'une page qu'on parcourt pour tout autre chose.

        Ce qui reste ici, c'est l'ÉTAT : quand on n'est pas en état de se
        battre, c'est une propriété du personnage, et elle se lit sur sa fiche.
      */}
      {p.pv <= 1 && (
        <section>
          <p className="tenu" style={{ fontSize: 12 }}>
            Tu n’es pas en état de te battre. Ça revient tout seul, lentement.
          </p>
        </section>
      )}

      {/*
        Les titres vivent sur la fiche et pas dans un onglet à eux : un nom est
        une propriété du personnage, au même titre que ses statistiques, et un
        onzième onglet pour dix lignes aurait coûté plus qu'il ne rapporte.
      */}
      <Titres id={id} surChangement={recharger} />

      <section>
        <button className="discret" onClick={surSortie}>Quitter</button>
      </section>
    </div>
  )
}
